/**
 * MessageContextMenu
 * Menu contestuale per azioni sui messaggi (reply, edit, delete, forward, react, pin)
 *
 * Disegnato in un portal con posizione fissa calcolata dal pulsante "...": prima era
 * `absolute` dentro la lista messaggi (overflow-x-hidden) e sui messaggi lunghi usciva
 * dal bordo sinistro tagliato. Ora sta sempre dentro lo schermo, sopra il messaggio se
 * c'e' spazio, altrimenti sotto.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Reply, Pencil, Trash2, Forward, Pin, PinOff, X, Users, Copy, Star, StarOff, CheckSquare } from 'lucide-react';
import { tip } from '@/components/messages/ChatTooltip';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const MARGIN = 8;

interface MessageContextMenuProps {
  messageId: string;
  isOutbound: boolean;
  isDeleted?: boolean;
  onReply: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  // Assenti quando il motore WhatsApp non li supporta: la voce non compare.
  onForward?: () => void;
  onForwardToTeamChat?: () => void;
  onReact: (emoji: string) => void;
  onPin?: () => void;
  /** Messaggio gia' fissato: la voce diventa "Sblocca" (usa onUnpin). */
  isPinned?: boolean;
  onUnpin?: () => void;
  /** Copia il testo negli appunti (assente se il messaggio non ha testo). */
  onCopy?: () => void;
  /** Stella: "Aggiungi a importanti" / "Togli da importanti". */
  onToggleStar?: () => void;
  isStarred?: boolean;
  /** Entra nella selezione multipla partendo da questo messaggio. */
  onSelect?: () => void;
  onClose: () => void;
  /** Pulsante "..." che ha aperto il menu: ancora della posizione (letta al momento). */
  anchorRef: React.RefObject<HTMLElement | null>;
  // Allineamento: i messaggi inviati (a destra) aprono il menu verso sinistra.
  position: 'left' | 'right';
  /** Senza riga delle reazioni (es. Instagram/Messenger sui nostri messaggi). */
  hideReactions?: boolean;
}

/** Pura: posizione del menu dentro la finestra. Esportata per i test. */
export function computeMenuPosition(
  anchor: { top: number; bottom: number; left: number; right: number },
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
  align: 'left' | 'right'
): { top: number; left: number } {
  let left = align === 'right' ? anchor.right - menu.width : anchor.left;
  left = Math.min(Math.max(left, MARGIN), viewport.width - menu.width - MARGIN);
  const spaceAbove = anchor.top - MARGIN;
  const fitsAbove = spaceAbove >= menu.height;
  let top = fitsAbove ? anchor.top - menu.height - 4 : anchor.bottom + 4;
  top = Math.min(Math.max(top, MARGIN), viewport.height - menu.height - MARGIN);
  return { top, left };
}

export function MessageContextMenu({
  isOutbound,
  isDeleted,
  onReply,
  onEdit,
  onDelete,
  onForward,
  onForwardToTeamChat,
  onReact,
  onPin,
  isPinned = false,
  onUnpin,
  onCopy,
  onToggleStar,
  isStarred = false,
  onSelect,
  onClose,
  anchorRef,
  position,
  hideReactions = false,
}: MessageContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const place = () => {
      const anchorEl = anchorRef.current;
      if (!ref.current || !anchorEl) return;
      const a = anchorEl.getBoundingClientRect();
      const m = ref.current.getBoundingClientRect();
      setCoords(
        computeMenuPosition(a, { width: m.width, height: m.height }, { width: window.innerWidth, height: window.innerHeight }, position)
      );
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchorRef, position]);

  // Chiudi cliccando fuori (il pulsante "..." gestisce da se' l'apri/chiudi), con Esc o
  // scorrendo la chat: un menu fisso non deve restare sospeso staccato dal messaggio.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    const onScroll = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose, anchorRef]);

  if (isDeleted) return null;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: coords?.top ?? -9999, left: coords?.left ?? -9999, visibility: coords ? 'visible' : 'hidden' }}
      className="z-[10001] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 py-1 min-w-[190px]"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Reazioni rapide */}
      {!hideReactions && (
      <div className="flex items-center gap-1 px-3 py-2 border-b border-[rgba(55,53,47,0.06)] dark:border-white/8">
        {QUICK_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => { onReact(emoji); onClose(); }}
            className="text-lg hover:scale-125 transition-transform"
            aria-label={`Reagisci con ${emoji}`}
            {...tip(`Reagisci con ${emoji}`)}
          >
            {emoji}
          </button>
        ))}
        <button
          onClick={() => { onReact(''); onClose(); }}
          className="ml-auto p-1 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10"
          aria-label="Togli la tua reazione"
          {...tip('Togli la tua reazione')}
        >
          <X className="w-3 h-3 text-[#787774]" />
        </button>
      </div>
      )}

      {/* Azioni */}
      <MenuItem icon={<Reply className="w-4 h-4" />} label="Rispondi" onClick={() => { onReply(); onClose(); }} />
      {isOutbound && onEdit && (
        <MenuItem icon={<Pencil className="w-4 h-4" />} label="Modifica" onClick={() => { onEdit(); onClose(); }} />
      )}
      {onForward && (
        <MenuItem icon={<Forward className="w-4 h-4" />} label="Inoltra a un'altra chat" onClick={() => { onForward(); onClose(); }} />
      )}
      {onForwardToTeamChat && (
        <MenuItem icon={<Users className="w-4 h-4" />} label="Inoltra ai colleghi (Team)" onClick={() => { onForwardToTeamChat(); onClose(); }} />
      )}
      {onCopy && (
        <MenuItem icon={<Copy className="w-4 h-4" />} label="Copia testo" onClick={() => { onCopy(); onClose(); }} />
      )}
      {isPinned && onUnpin ? (
        <MenuItem icon={<PinOff className="w-4 h-4" />} label="Sblocca" onClick={() => { onUnpin(); onClose(); }} />
      ) : !isPinned && onPin ? (
        <MenuItem icon={<Pin className="w-4 h-4" />} label="Fissa in alto" onClick={() => { onPin(); onClose(); }} />
      ) : null}
      {onToggleStar && (
        <MenuItem
          icon={isStarred ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
          label={isStarred ? 'Togli da importanti' : 'Aggiungi a importanti'}
          onClick={() => { onToggleStar(); onClose(); }}
        />
      )}
      {onSelect && (
        <MenuItem icon={<CheckSquare className="w-4 h-4" />} label="Seleziona" onClick={() => { onSelect(); onClose(); }} />
      )}
      {isOutbound && onDelete && (
        <MenuItem
          icon={<Trash2 className="w-4 h-4" />}
          label="Elimina per tutti"
          onClick={() => { onDelete(); onClose(); }}
          danger
        />
      )}
    </div>,
    document.body
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 ${
        danger ? 'text-red-500 dark:text-red-400' : 'text-[#37352F] dark:text-white'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
